/**
 * Selections for the things a row of tiles can hold, shared by `screenQuery`,
 * `screenSkeletonQuery` and `containerQuery`. Each of those documents decides which
 * of these it spreads; a document that needs more than a fragment offers extends it
 * rather than restating it (see `ContainerMediaMetadataItem` in container.ts).
 */
import { imageFragment } from './fragments';

export const editorialImageFieldsFragment = `
  fragment EditorialImageFields on EditorialImage {
    url
    altText
    breakpoint
  }
`;

/** `LinkImage` as a promo banner / featured link. */
export const linkImageFieldsFragment = `
  fragment LinkImageFields on LinkImage {
    __typename
    id
    displayTitle
    widthStyle
    action {
      __typename
      ... on URL {
        link
      }
      ... on Screen {
        id
        path
      }
      ... on MediaMetadata {
        id
        path
      }
      ... on ContentMetadata {
        id
        path
      }
      ... on CollectionMetadata {
        id
      }
      ... on Container {
        id
      }
    }
    imagesByBreakpoint {
      ...EditorialImageFields
    }
    image {
      ...EditorialImageFields
    }
    hoverImage {
      ...EditorialImageFields
    }
  }
`;

/**
 * `LinkImage` as a row item. Kept separate from `LinkImageFields` because tiles
 * have no use for the per-breakpoint image sets a promo banner needs, and a row can
 * hold many of them.
 */
export const linkImageItemFragment = `
  fragment LinkImageItem on LinkImage {
    id
    displayTitle
    widthStyle
    action {
      __typename
      ... on Screen {
        id
        title
        path
        channelName
      }
      ... on Container {
        id
      }
      ... on URL {
        link
      }
      ... on MediaMetadata {
        id
      }
      ... on ContentMetadata {
        id
      }
      ... on CollectionMetadata {
        id
      }
    }
    image {
      ...EditorialImageFields
    }
    hoverImage {
      ...EditorialImageFields
    }
  }
`;

export const liveItemFragment = `
  fragment LiveItem on Live {
    id
    channelGroups {
      groupId
      groupName
      channels {
        channelId
        streamContent {
          path
          title
          id
          shortDescription
          locked
        }
        events {
          eventTitle
          eventSubTitle
          vodContent {
            id
          }
          startTimeUtc
          endTimeUtc
          isLive
        }
      }
    }
    __typename
  }
`;

export const mediaMetadataItemFragment = `
  fragment MediaMetadataItem on MediaMetadata {
    id
    title
    path
    metadataImages {
      poster {
        ...ImageFragment
      }
      thumbnail {
        ...ImageFragment
      }
      promoTeaserLarge {
        url
      }
      square {
        ...ImageFragment
      }
    }
    badges {
      label
      detailedLabel
    }
    flags {
      detailedLabel
      label
      flag
    }
    originatingNetworkLogo {
      ...ImageFragment
    }
    mediaType
    shortDescription
    productionYear
    maturityRatings {
      id
      displayName
    }
    parentalControl {
      displaySubTitle
      displayTitle
      locked
    }
    genres {
      displayName
    }
    locked
    seasons {
      title
      id
      seasonNumber
    }
  }
`;

/**
 * `parentalControl` is selected on the content itself as well as on `media` — live
 * channel tiles read it off `streamContent`, which is a `ContentMetadata`. So does
 * `lockDetails`: `navigateToLiveChannel` sends the viewer to the player when it is
 * absent, so a document that omits it can walk past a lock.
 */
export const contentMetadataItemFragment = `
  fragment ContentMetadataItem on ContentMetadata {
    id
    title
    path
    seasonNumber
    episodeNumber
    contentType
    badge {
      label
      detailedLabel
    }
    flags {
      detailedLabel
      label
      flag
    }
    languageIndicators {
      indicator
      indicatorLabel
      languages {
        langCode
        displayTitle
        locked
      }
    }
    media {
      ...MediaMetadataItem
    }
    metadataImages {
      poster {
        ...ImageFragment
      }
      thumbnail {
        ...ImageFragment
      }
      square {
        ...ImageFragment
      }
      promoTeaserLarge {
        url
      }
      channelLogo {
        imageType
        url
      }
    }
    shortDescription
    maturityRatings {
      id
      displayName
    }
    parentalControl {
      displaySubTitle
      displayTitle
      locked
    }
    lockDetails {
      action
      ctaLabel
    }
    locked
    duration {
      timeLabel
      timeAccessibilityLabel
    }
  }
`;

export const collectionMetadataItemFragment = `
  fragment CollectionMetadataItem on CollectionMetadata {
    id
    title
    collectionType
    thumbnail {
      ...ImageFragment
    }
  }
`;

export const linkTextItemFragment = `
  fragment LinkTextItem on LinkText {
    id
    displayTitle
    description
    action {
      __typename
      ... on URL {
        link
      }
      ... on Screen {
        id
        path
        title
      }
      ... on MediaMetadata {
        id
        path
      }
      ... on ContentMetadata {
        id
      }
      ... on CollectionMetadata {
        id
      }
      ... on Container {
        id
      }
    }
  }
`;

export const liveChannelItemFragment = `
  fragment LiveChannelItem on LiveChannel {
    channelId
    streamContent {
      ...ContentMetadataItem
    }
    events {
      eventId
      eventTitle
      eventSubTitle
      eventShortDescription
      startTimeUtc
      endTimeUtc
      eventImage {
        url
        altText
      }
      eventScheduleType
      eventPage {
        primaryCta
      }
      tmsId
    }
  }
`;

/**
 * Every `LinkButton` CTA: promotional teaser buttons and
 * `featuredRowConfig.featuredBlock.cta`.
 */
export const linkButtonItemFragment = `
  fragment LinkButtonItem on LinkButton {
    __typename
    id
    displayTitle
    buttonStyle
    action {
      __typename
      ... on URL {
        link
      }
      ... on Screen {
        id
        path
      }
      ... on MediaMetadata {
        id
        title
        path
        parentalControl {
          locked
          displayTitle
          displaySubTitle
        }
      }
      ... on ContentMetadata {
        id
        title
        locked
        path
        lockDetails {
          action
          ctaLabel
          __typename
        }
        media {
          id
          title
          parentalControl {
            locked
            displayTitle
            displaySubTitle
          }
        }
      }
      ... on CollectionMetadata {
        id
      }
      ... on Container {
        id
      }
      ... on BaseContainer {
        id
        path
      }
    }
  }
`;

export const promotionalTeaserItemFragment = `
  fragment PromotionalTeaserItem on PromotionalTeaser {
    displayTitle
    summary
    promotedItem {
      __typename
      ... on Screen {
        id
        path
        title
      }
      ...MediaMetadataItem
      ...ContentMetadataItem
      ...CollectionMetadataItem
      ... on Container {
        id
      }
    }
    item {
      __typename
      ...MediaMetadataItem
      ...ContentMetadataItem
    }
    tuneInInfo
    cta {
      label
      action
    }
    buttons {
      ...LinkButtonItem
    }
    images {
      ...ImageFragment
    }
    sponsorship {
      layoutType
      displayTitle
      logos {
        id
        image {
          url
          altText
        }
      }
    }
  }
`;

/**
 * GraphQL rejects a document that defines a fragment it never spreads, so a document
 * may only carry the fragments it actually uses. Documents that inline their own
 * `LinkImage` selection take this bundle; the rest append `linkImageItemFragment`.
 */
export const itemFragmentsWithoutLinkImageItem = [
  imageFragment,
  editorialImageFieldsFragment,
  linkImageFieldsFragment,
  mediaMetadataItemFragment,
  contentMetadataItemFragment,
  collectionMetadataItemFragment,
  linkTextItemFragment,
  liveChannelItemFragment,
  linkButtonItemFragment,
  promotionalTeaserItemFragment,
].join('');

export const itemFragments = `${itemFragmentsWithoutLinkImageItem}${linkImageItemFragment}`;
